import { type JSX } from "react";
import { Image, RichText, type LinkField } from "@sitecore-content-sdk/nextjs";
import { SitecoreLink as Link } from "../../../atoms/SitecoreLink";
import Button from "../../../atoms/Button";
import RichTextWithTable from "../../../molecules/RichTextWithTable/RichTextWithTables";
import { extractHiddenBlock } from "../../../utils/styles";
import { NoDataFallback } from "../../../utils/NoDataFallback";
import styles from "./Promo.module.scss";
import { PromoProps } from "components/sxa/promo/promo.props";

interface PromoCtaButtonProps {
  field?: LinkField;
  defaultButtonType?: string;
  className?: string;
  "data-testid"?: string;
}

const PromoCtaButton = ({
  field,
  defaultButtonType = "",
  className = "",
  "data-testid": dataTestid,
}: PromoCtaButtonProps): JSX.Element | null => {
  if (!field) return null;

  const linkClass = field.value?.class;
  const buttonType = linkClass || defaultButtonType;

  return (
    <Button
      buttonType={buttonType}
      className={className}
      field={field}
      iconName={linkClass ? "CHEVRON_BLUE_RIGHT" : ""}
      tabIndex={0}
      aria-label={field.value?.text}
      data-role="link"
      data-testid={dataTestid}
    />
  );
};

export const Default = (props: PromoProps): JSX.Element => {
  console.log("---->[Promo:Default] Component", props.fields);

  const { fields, params } = props;

  if (!fields) {
    return <NoDataFallback componentName="Promo" />;
  }

  const id = params?.RenderingIdentifier;
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  return (
    <div
      className={`defaultpromo-component ${params?.Styles ?? ""} ${hiddenBlockStyles}`}
      id={id ?? undefined}
      data-testid="default-component"
      data-role="promo-component"
    >
      <div className="component-content">
        <div className="field-promoicon">
          <Image
            field={fields.PromoIcon}
            data-testid="promo-icon"
            imageParams={{ mw: 1200 }}
          />
        </div>
        <div className="promo-text">
          <div>
            <div className="field-promotext" data-testid="promo-text">
              <RichTextWithTable
                field={fields.PromoText}
                className="richtext-content"
              />
            </div>
          </div>
          <div className="field-promolink" data-testid="promo-link">
            <PromoCtaButton field={fields.PromoLink} />
          </div>
        </div>
      </div>
    </div>
  );
};

export const WithText = (props: PromoProps): JSX.Element => {
  console.log("---->[Promo:WithText] Component", props.fields);

  const { fields, params } = props;

  if (!fields) {
    return <NoDataFallback componentName="Promo" />;
  }

  const id = params?.RenderingIdentifier;
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  return (
    <div
      className={`promowithtext-component ${params?.Styles ?? ""} ${hiddenBlockStyles}`}
      id={id ?? undefined}
      data-testid="withText-component"
      data-role="promo-component"
    >
      <div className="component-content">
        <div className="field-promoicon" data-testid="promo-icon">
          <Image field={fields.PromoIcon} imageParams={{ mw: 1200 }} />
        </div>
        <div className="promo-text">
          <div>
            <div className="field-promotext" data-testid="promo-text">
              <RichText
                className="richtext-content promo-text"
                field={fields.PromoText}
              />
            </div>
          </div>
          <div className="field-promotext" data-testid="promo-text2">
            <RichText
              className="richtext-content promo-text"
              field={fields.PromoText2}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const BasicPromo = (props: PromoProps): JSX.Element => {
  console.log("---->[Promo:Basic] Component", props.fields);

  const { fields, params } = props;

  if (!fields) {
    return <NoDataFallback componentName="Promo" />;
  }

  const id = params?.RenderingIdentifier;
  const stylesArray = params?.Styles?.split(" ") ?? [];
  const hasTextWhite = stylesArray.includes("text-white");
  const withTextPromoCta = stylesArray.includes("basic-promo-cta");
  const textColor = hasTextWhite ? "text-white" : "text-gray";
  const ctaVariant = hasTextWhite ? "primary-reverse" : "primary";
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  const wrapperClass = withTextPromoCta
    ? "basicpromowithcta-component"
    : "basicpromo-component mb-[var(--global-component-spacing)]";

  const titleClass = withTextPromoCta
    ? `${styles.promoTextCta} ${textColor} text-[32px] !leading-[40px] tracking-[0] font-medium m-0`
    : `${styles.promoText} w-full lg:!text-[42px] lg:leading-[52px] !text-[32px] leading-[38px] text-ecolab-blue text-center not-italic font-medium z-10 tracking-[0.4px] m-0`;

  const containerClass = withTextPromoCta
    ? "lg:pt-[64px] px-[var(--spacing-sixteen)] pb-[60px] lg:max-w-[438px] relative min-h-[1px]"
    : "w-full mx-auto flex flex-col lg:flex-row justify-center";

  const innerBlockClass = withTextPromoCta
    ? "leading-[1.5] tracking-[0.4px] mb-[40px] lg:mb-[20px] m-0 font-normal"
    : "lg:max-w-[586px] lg:pl-[27px] lg:pr-0 lg:border-l-[2px] lg:border-teal lg:border-t-0 lg:leading-[30px] inline-block w-full pl-[44px] pr-[24px] m-0 leading-[28px] font-normal text-left text-gray tracking-[0]";

  return (
    <div
      className={`${styles.promo} ${wrapperClass} ${hiddenBlockStyles}`}
      id={id ?? undefined}
      data-testid={
        withTextPromoCta ? "banner-component" : "content-block-component"
      }
      data-role="promo-component"
    >
      <div className={containerClass}>
        <div
          className={
            withTextPromoCta
              ? "relative mb-[20px] pb-[20px] m-0 p-0"
              : "lg:flex lg:justify-center lg:items-center lg:mr-[80px] lg:p-0 pb-[34px]"
          }
        >
          <RichText
            tag="h2"
            className={`richtext-content ${titleClass}`}
            field={fields.PromoText}
            data-testid="promo-title"
          />
        </div>

        <div className={innerBlockClass}>
          <RichText
            tag="p"
            className={`richtext-content z-10 mb-[10px] ${
              withTextPromoCta ? `${textColor} text-base` : "text-xl"
            }`}
            field={fields.PromoText2}
            data-testid="promo-sub-title"
          />
        </div>
        {withTextPromoCta && (
          <PromoCtaButton
            field={fields.PromoLink}
            defaultButtonType={ctaVariant}
            className="z-[1] !mr-0  "
            data-testid="promo-link"
          />
        )}
      </div>
    </div>
  );
};

export const WithTextPromoCta = (props: PromoProps): JSX.Element => {
  console.log("---->[Promo:WithTextCta] Component", props.fields);

  const { fields, params } = props;

  if (!fields) {
    return <NoDataFallback componentName="Promo" />;
  }

  const id = params?.RenderingIdentifier;
  const bgColor = params?.Styles ?? "";
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  return (
    <div
      className={`${styles.promo} ${bgColor} text-white withTextCta-component ${hiddenBlockStyles}`}
      id={id ?? undefined}
      data-testid="withTextCta-component"
      data-role="promo-component"
    >
      <div className="lg:pt-[64px] px-[var(--spacing-sixteen)] pb-[60px] lg:max-w-[438px] relative min-h-[1px]">
        <div className="relative mb-[20px] pb-[20px] m-0 p-0">
          <RichText
            tag="h2"
            className={`richtext-content ${styles.promoTextCta} text-[32px] !leading-[40px] tracking-[0] font-medium m-0`}
            field={fields.PromoText}
            data-testid="promo-text"
          />
        </div>
        <div className="text-base leading-[1.5] tracking-[0.4px] mb-[40px] lg:mb-[20px] m-0 font-normal">
          <RichText
            tag="p"
            className="richtext-content z-10"
            field={fields.PromoText2}
            data-testid="promo-text2"
          />
        </div>
        {fields.PromoLink && (
          <Link
            className="z-[1] mr-[10px]"
            field={fields.PromoLink}
            data-testid="promo-link"
            data-role="link"
          />
        )}
      </div>
    </div>
  );
};

export const SectionHeader = (props: PromoProps): JSX.Element => {
  console.log("---->[Promo:SectionHeader] Component", props.fields);

  const { fields, params } = props;

  if (!fields) {
    return <NoDataFallback componentName="Promo" />;
  }

  const id = params?.RenderingIdentifier;
  const descStyles = params?.Styles || "text-text-gray";
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  return (
    <div
      id={id ?? undefined}
      className={`promo--section-header px-[var(--spacing-sixteen)] mb-[var(--global-component-spacing)] ${styles.sectionHeaderPromo} ${params?.Styles ?? ""} ${hiddenBlockStyles}`}
      data-testid="sectionHeader-component"
      data-role="promo-component"
    >
      {fields.PromoText?.value && (
        <RichText
          tag="h1"
          field={fields.PromoText}
          className={`${styles.promoSectionHeaderHeader} richtext-content relative text-ecolab-blue text-[34px] leading-[42.16px] lg:text-[42px] lg:leading-[52.08px] tracking-[0] font-medium m-0 pb-[15px] pr-[var(--spacing-sixteen)] min-h-[1px]`}
          data-testid="header-title"
        />
      )}
      {fields.PromoText2?.value && (
        <RichText
          tag="h2"
          field={fields.PromoText2}
          className="richtext-content text-text-gray text-[16px] font-normal tracking-[0.4px] lg:leading-[24px] lg:tracking-[0.8px] font-medium mb-[10px] mt-[20px] pr-[var(--spacing-sixteen)] min-h-[1px]"
          data-testid="sub-header"
        />
      )}
      {fields.PromoText3?.value && (
        <div
          className={`mb-[20px] text-base tracking-[0.4px] leading-[24px] lg:tracking-[0.8px] font-normal mt-[0px] pr-[var(--spacing-sixteen)] pt-[20px] min-h-[80px] ${descStyles}`}
        >
          <RichText
            field={fields.PromoText3}
            data-testid="description-text"
            className="richtext-content"
          />
        </div>
      )}
      {fields.PromoLink?.value?.text && (
        <div className="mb-[20px] pr-[var(--spacing-sixteen)] flex justify-start">
          <PromoCtaButton
            field={fields.PromoLink}
            defaultButtonType="primary"
            className="tracking-[0.4px]"
            data-testid="promo-link"
          />
        </div>
      )}
    </div>
  );
};
